import * as React from "react";
import { useMe } from "@/api/me";
import { useAuthStore } from "@/store/authStore";
import { brandingAssetUrl } from "@/lib/brandingAssets";
import { GROUND_DARK, GROUND_LIGHT, resolveActionColour } from "@/lib/contrast";

/**
 * Reads the `branding` block from /me and applies it to the page:
 *
 *   - `--brand-primary` / `--brand-secondary` CSS vars on :root
 *   - Derived `--brand-*-hover` + `--brand-*-fg` (color-mix + luminance) so
 *     palette shifts from defaults don't leave hover/foreground contrast
 *     stuck on the platform-default values.
 *   - `document.title` prefix ("{app_name} · Monitoring")
 *   - `<link rel="icon">` swap when favicon_s3_key is set
 *
 * Theme awareness (MV2-837, ds/WhiteLabelAction):
 *   `--brand-primary` is the tenant's primary colour in BOTH themes — the
 *   old dark-mode swap to their secondary is gone (decision 4 Sep: a brand
 *   should not change hue when the theme toggles). What changes per theme is
 *   the derived ACTION set: `--action` / `--action-fg` / `--action-hover` /
 *   `--action-edge` / `--focus` come from `resolveActionColour`, which keeps
 *   the tenant's hue and guarantees legibility by tier — pick the ink, give it
 *   an edge, and only as a last resort move lightness away from the page.
 *   A MutationObserver watches the `.dark` class so a theme toggle re-derives
 *   without a page reload. The platform default is recognised by its palette
 *   and skipped: its designed navy-on-light / green-on-dark pair lives in
 *   tokens.css and must never be derived.
 *
 * Precedence resolution happens server-side in
 * `app.core.branding.resolve_branding`: customer > client > platform.
 */
export function BrandingProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const status = useAuthStore((s) => s.status);
  const { data } = useMe({ enabled: status === "authenticated" });

  // MV2-844 (ds/WhiteLabelSwap, CROSS_PAGE §13): a LAYOUT effect, so the
  // CSS variables, title and favicon land in the same frame as the React
  // tree that renders the new tenant's logo and data. A passive effect
  // could paint one frame of Client A's colours over Client B's rows.
  React.useLayoutEffect(() => {
    const root = document.documentElement;

    if (!data?.branding) {
      // No branding to apply — e.g. mid-fetch right after `actingAs`
      // changes (the queryKey swap drops `data` to undefined with no
      // placeholderData configured), or unauthenticated. Reset to the
      // platform-neutral state instead of leaving the *previous*
      // tenant's branding rendered during the switch (CROSS_PAGE_
      // DECISIONS §13 — no stale-branding flash across impersonation).
      root.style.removeProperty("--brand-primary");
      root.style.removeProperty("--brand-secondary");
      root.style.removeProperty("--brand-primary-hover");
      root.style.removeProperty("--brand-primary-fg");
      root.style.removeProperty("--brand-secondary-hover");
      root.style.removeProperty("--brand-secondary-fg");
      for (const v of ACTION_VARS) root.style.removeProperty(v);
      document.title = DEFAULT_TITLE;
      setFavicon(DEFAULT_FAVICON);
      return;
    }
    const b = data.branding;

    // ds/Main: re:bot's own palette is a DESIGNED pair — navy carries the
    // filled control on light, green on dark. Deriving an action colour from
    // navy against the dark page lands on a desaturated mid-blue that is not
    // re:bot. /me returns the platform block whenever no tenant overrides it,
    // so detect it here and let tokens.css own every brand/action variable.
    if (isPlatformPalette(b)) {
      for (const v of BRAND_VARS) root.style.removeProperty(v);
      for (const v of ACTION_VARS) root.style.removeProperty(v);
      document.title = `${b.app_name} · Monitoring`;
      setFavicon(brandingAssetUrl(b.favicon_s3_key) ?? DEFAULT_FAVICON);
      return;
    }

    function apply() {
      const isDark = root.classList.contains("dark");
      const primary = b.primary_color;

      // Identity: the tenant's colours as chosen, no theme swap.
      root.style.setProperty("--brand-primary", primary);
      root.style.setProperty("--brand-secondary", b.secondary_color);
      root.style.setProperty(
        "--brand-primary-hover",
        `color-mix(in srgb, ${primary} 85%, ${isDark ? "black" : "white"})`,
      );
      root.style.setProperty("--brand-primary-fg", fgOn(primary));
      root.style.setProperty(
        "--brand-secondary-hover",
        `color-mix(in srgb, ${b.secondary_color} 85%, ${isDark ? "black" : "white"})`,
      );
      root.style.setProperty("--brand-secondary-fg", fgOn(b.secondary_color));

      // The filled control: legible on this theme's page, hue kept. When the
      // hex cannot be parsed the platform default in tokens.css stays.
      const action = resolveActionColour(
        primary,
        isDark ? GROUND_DARK : GROUND_LIGHT,
      );
      if (!action) {
        for (const v of ACTION_VARS) root.style.removeProperty(v);
        return;
      }
      root.style.setProperty("--action", action.fill);
      root.style.setProperty("--action-fg", action.ink);
      root.style.setProperty(
        "--action-hover",
        `color-mix(in srgb, ${action.fill} 85%, ${isDark ? "black" : "white"})`,
      );
      root.style.setProperty("--action-edge", action.edge);
      root.style.setProperty("--focus", action.fill);
    }

    apply();

    // Theme toggle → re-derive. uiStore stores preference but the actual
    // .dark class is flipped on <html> at paint time (see index.html +
    // ThemeToggle). Watching the classList covers both manual toggles and
    // system-preference changes with theme="system".
    const obs = new MutationObserver((muts) => {
      if (muts.some((m) => m.attributeName === "class")) apply();
    });
    obs.observe(root, { attributes: true, attributeFilter: ["class"] });

    document.title = `${b.app_name} · Monitoring`;

    // Favicon swap. R2 fix: when the new branding has no favicon (e.g.
    // exiting impersonation back to a tenant without one, or to the
    // platform default), we MUST reset to the bundled platform favicon
    // rather than leaving the previous tenant's favicon pinned.
    const faviconUrl = brandingAssetUrl(b.favicon_s3_key);
    setFavicon(faviconUrl ?? DEFAULT_FAVICON);

    return () => obs.disconnect();
  }, [data]);

  return <>{children}</>;
}

// Matches index.html's <link rel="icon" href="/brand/SVG/Light_rounded_icon.svg">
// — reset-to when no tenant favicon is configured, or mid-fetch (see above).
const DEFAULT_FAVICON = "/brand/SVG/Light_rounded_icon.svg";
// Matches index.html's <title> — reset-to when there's no branding to apply.
const DEFAULT_TITLE = "RenewableBot Monitoring";
/** Platform row in branding_configs (monitoring-backend app/core/branding.py). */
const PLATFORM_PRIMARY = "#0c2d49";
const PLATFORM_SECONDARY = "#39b766";
function isPlatformPalette(b: {
  primary_color: string;
  secondary_color: string;
}): boolean {
  return (
    b.primary_color.toLowerCase() === PLATFORM_PRIMARY &&
    b.secondary_color.toLowerCase() === PLATFORM_SECONDARY
  );
}
const BRAND_VARS = [
  "--brand-primary",
  "--brand-secondary",
  "--brand-primary-hover",
  "--brand-primary-fg",
  "--brand-secondary-hover",
  "--brand-secondary-fg",
] as const;
/** The derived action set — cleared together so tokens.css takes over. */
const ACTION_VARS = [
  "--action",
  "--action-fg",
  "--action-hover",
  "--action-edge",
  "--focus",
] as const;

/** Returns black or white — whichever has better contrast against `hex`. */
function fgOn(hex: string): string {
  const parsed = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!parsed) return "#ffffff";
  const n = parseInt(parsed[1]!, 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? "#0b1a11" : "#ffffff";
}

function setFavicon(href: string): void {
  // MV2-128 / MV2-133: mutating the existing `<link rel='icon'>`'s
  // `href` is the well-known browser-flaky pattern — Safari + Chrome
  // both cache favicons by element identity and treat in-place href
  // changes as no-ops, so impersonation flips left the previous
  // tenant's icon pinned in the tab. Remove every existing icon link
  // and append a fresh element so the browser re-fetches every time.
  // Cheap: this fires once per branding change, not per render.
  document
    .querySelectorAll<HTMLLinkElement>("link[rel~='icon']")
    .forEach((el) => {
      el.parentNode?.removeChild(el);
    });
  const link = document.createElement("link");
  link.rel = "icon";
  link.href = href;
  document.head.appendChild(link);
}
